import React, {
  createContext,
  KeyboardEventHandler,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import debounce from 'lodash-es/debounce';
import { InjectedIntl } from 'react-intl';

import AkAvatar from '@atlaskit/avatar';
import Button, { ButtonGroup } from '@atlaskit/button';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import { ImageUploadProvider } from '@atlaskit/editor-common/provider-factory';
import {
  EditorActions,
  EditorProps,
  WithEditorActions,
  CollapsedEditor,
  ToolbarHelp,
} from '@atlaskit/editor-core';
import { ComposableEditor as AkComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { JSONNode } from '@atlaskit/editor-json-transformer';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { MentionProvider } from '@atlaskit/mention/resource';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
// TODO fix this import error
// eslint-disable-next-line import/extensions, import/no-unresolved
import { FeedbackMetadata } from '@atlassian/editor-plugin-ai/dist/types/types';
import { NextEditorPluginAI } from '@atlassian/editor-plugin-ai/EditorPluginAI';

import { ClickEventWithSubjectArgs } from 'src/components/settings/analytics';
import { useStatsigGate } from 'src/contexts/feature-provider';

import { AiOptInModal } from './ai-opt-in-modal';
import messages from './bbc-atlaskit-editor.i18n';
import { codeSuggestionsPlugin } from './extensions/code-suggestions/code-suggestions-toolbar-plugin';
import { getCodeSuggestionsManifest } from './extensions/code-suggestions/manifest';
import FeedbackCollector, {
  FeedbackCollectorContext,
} from './feedback-collector';
import {
  PluginOptions,
  useEditorProviderAI,
} from './hooks/use-editor-plugin-ai';
import store from './local-storage';
import { createContextIdentifierProvider } from './providers/context-identifier-provider';
import { smartCardProvider } from './providers/smart-card-provider';
import { Container, AvatarSection, EditorSection } from './styles';
import { ConversationsMentionContext } from './types/ConversationsMentionContext';
import { FabricUser } from './types/User';

// See https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
// https://developer.mozilla.org/en-US/docs/Web/API/Event/returnValue
interface UnloadEvent extends Event {
  returnValue: any;
}

const EDITOR_CONFIG = {
  allowAnalyticsGASV3: true,
  allowTables: {
    advanced: false,
    isHeaderRowRequired: true,
    allowControls: true,
  },
  textFormatting: {
    disableSuperscriptAndSubscript: true,
    disableUnderline: true,
  },
  performanceTracking: {
    bFreezeTracking: {
      trackInteractionType: true,
      trackSeverity: true,
    },
    catchAllTracking: {
      enabled: false,
    },
    contentRetrievalTracking: {
      enabled: true,
      failureSamplingRate: 1,
      reportErrorStack: false,
      successSamplingRate: 5,
    },
    inputTracking: {
      countNodes: true,
      // @ts-ignore disable inputTracking for PDV env (see https://atlassian.slack.com/archives/CFH7BMNFN/p1693799165533639)
      enabled: !window.Cypress,
      samplingRate: 100,
      severityDegradedThreshold: 151,
      severityNormalThreshold: 76,
      trackSeverity: true,
    },
    ttiTracking: {
      // @ts-ignore disable inputTracking for PDV env (see https://atlassian.slack.com/archives/CFH7BMNFN/p1693799165533639)
      enabled: !window.Cypress,
      trackSeverity: true,
      ttiFromInvocationSeverityDegradedThreshold: 11000,
      ttiFromInvocationSeverityNormalThreshold: 3000,
      ttiSeverityDegradedThreshold: 10000,
      ttiSeverityNormalThreshold: 8000,
    },
  },
};

// Function to check if there are duplicate editor plugins
// This is currently checked inside the editor, but surfacing it here
// to fallback in the case that there is a duplicate plugin due to dangerouslyAppendPlugins
export function hasDuplicates(preset: EditorPresetBuilder): boolean {
  const pluginNames = preset.build().map(plugin => plugin.name);
  return new Set(pluginNames).size !== pluginNames.length;
}

// This is a stop-gap for preventing the user from losing their work. Eventually
// this will be replaced with drafts/auto-save functionality
export function beforeUnloadHandler(e: UnloadEvent): string {
  // The beforeUnload dialog is implemented inconsistently.
  // The following is the most cross-browser approach.
  const confirmationMessage =
    'You have an unsaved comment. Are you sure you want to leave without saving?';
  e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+
  return confirmationMessage; // Gecko, WebKit, Chrome <34
}

type EditorPluginAIConfig = Parameters<typeof NextEditorPluginAI>[0]['config'];

type ComposableEditorProps = {
  editorProps: EditorProps;
  placeholder?: string;
  isExpanded?: boolean;
  handleOpen: () => void;
  multiCommentKeyboardShortcutEnabled?: boolean;
  editorPluginAIProvider: EditorPluginAIConfig['editorPluginAIProvider'] | null;
  aiGlobalOptIn: EditorPluginAIConfig['aiGlobalOptIn'];
  fileMeta?: any;
  codeSuggestionsFF: boolean;
};

/**
 * This function serves as a wrapper to building the CollapsedEditor with
 * a ComposableEditor child. This allows us to fix the keyboard shortcuts
 * for starting a review or adding a comment to a review. We move the CollapsedEditor
 * here because it can only have a child of 3 specific elements, so putting a
 * functional wrapper component around the ComposableEditor was not an option.
 * (https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence-frontend/browse/platform/packages/editor/editor-core/src/ui/CollapsedEditor/index.tsx#44)
 * @param props
 * @returns JSX.Element
 */
export function AkCollapsibleComposableEditor(
  props: ComposableEditorProps
): JSX.Element {
  const {
    editorProps,
    multiCommentKeyboardShortcutEnabled,
    placeholder,
    isExpanded,
    handleOpen,
    editorPluginAIProvider,
    aiGlobalOptIn,
    fileMeta,
    codeSuggestionsFF,
  } = props;

  const { dangerouslyAppendPlugins, ...editorNewProps } = editorProps;

  const universalPreset = useUniversalPreset({
    props: editorNewProps,
  });

  const shouldAddAIPlugin = Boolean(editorPluginAIProvider);

  const { preset, editorApi } = usePreset(
    () =>
      universalPreset
        .maybeAdd(
          [
            NextEditorPluginAI,
            {
              aiGlobalOptIn,
              // `editorPluginAIProvider` will never be `null` here as we already check it
              // in the line below, but TS requires a non-null provider here.
              editorPluginAIProvider: editorPluginAIProvider!,
            },
          ],
          shouldAddAIPlugin
        )
        .maybeAdd(codeSuggestionsPlugin, codeSuggestionsFF),
    [universalPreset, shouldAddAIPlugin]
  );

  const { onSave, onCancel, ...remainingProps } = editorNewProps;

  // check if the comment is an inline comment
  const isInlineComment = fileMeta?.to || fileMeta?.from;

  const extensionProviders = [
    new DefaultExtensionProvider([getCodeSuggestionsManifest(fileMeta)]),
  ];
  // Drop the onSave and onCancel if this is multicomment because the shortcut
  // is set up in useUniversalPreset and leaving these in props will cause
  // the Save and Cancel buttons to appear.
  const composableProps = multiCommentKeyboardShortcutEnabled
    ? remainingProps
    : editorNewProps;
  return (
    <CollapsedEditor
      placeholder={placeholder}
      isExpanded={isExpanded}
      onFocus={handleOpen}
    >
      <AkComposableEditor
        preset={hasDuplicates(preset) ? universalPreset : preset}
        {...composableProps}
        primaryToolbarComponents={
          composableProps.allowHelpDialog
            ? [
                <ToolbarHelp
                  key="help"
                  // @ts-expect-error - When we upgrade the editor in the future we're going to need to pass the editor-api to the
                  // ToolbarHelp component. However, for now this does nothing. We have pre-emptively fixed this to ease the future upgrade.
                  // Once the upgrade is done, we can remove this comment and the ts-expect-error.
                  editorApi={editorApi}
                />,
              ]
            : undefined
        }
        extensionProviders={
          codeSuggestionsFF && isInlineComment ? extensionProviders : undefined
        }
      />
    </CollapsedEditor>
  );
}

export interface Props {
  /** The default value that will be rendered in the editor when mounted,
   * unless `draftLocalStorageKey` is provided and a draft is present in local
   * storage. Saved drafts take precedence over the default value. */
  defaultValue?: any;
  /** Whether the editor is expanded on mount. Defaults to false. */
  isExpanded?: boolean;
  /** A function that will be called when the editor's "Cancel" button is
   * clicked. */
  onCancel?: () => void;
  /** A function that will be called after an editor's action button's action
   * completes successfully. */
  onSave?: (value: any, pending: boolean) => void;
  /** A function that will be called when the editor is closed. This is called
   * both after cancelation and after saving. */
  onClose?: () => void;
  /** A function that will be called when the editor is opened. This is called
   * both when expanded from a collapsed state or when first rendered in an
   * expanded state. */
  onOpen?: () => void;
  /** A function that will be called when users are able to interact with the
   * editor. The function will be provided with an `EditorActions` object that
   * holds functions that may be used to query and manipulate the editor. This
   * property is passed through to the underlying AtlasKit editor.
   * See also {@link https://atlaskit.atlassian.com/packages/editor/editor-core|the AtlasKit editor docs}. */
  onEditorReady?: (actions: EditorActions) => void;
  isEditing?: boolean;
  onChange?: (value: any) => void;
  showBeforeUnloadWarning?: boolean;
  allowEmptyValue?: boolean;
  onEscapeHandler?: KeyboardEventHandler;
  /* eslint react/no-unused-prop-types: "warn" */
  owner: string;
  /* eslint react/no-unused-prop-types: "warn" */
  repoSlug: string;
  intl: InjectedIntl;
  conversationsMentionContext: ConversationsMentionContext;
  user?: FabricUser;
  currentUserAccountId?: string;
  /** Determines whether the button group under the editor should be hidden.
   * This toolbar normally contains action buttons such as "Save" and "Cancel".
   */
  hideSecondaryToolbar?: boolean;
  /** Determines whether the editor should be focused. Defaults to true. This
   * is passed to the underlying AtlasKit editor object. See also {@link https://atlaskit.atlassian.com/packages/editor/editor-core|the AtlasKit editor docs}. */
  shouldFocus?: boolean;
  /** Determines whether users can insert a horizontal rule in the editor.
   * Defaults to false. This is passed to the underlying AtlasKit editor
   * object. See also {@link https://atlaskit.atlassian.com/packages/editor/editor-core|the AtlasKit editor docs}. */
  allowRule?: boolean;
  popupsMountPoint?: HTMLElement;

  // ======
  // Editor
  // ======
  placeholder?: string;
  /* eslint react/no-unused-prop-types: "warn" */
  disableScrollTo?: boolean;
  allowHelpButton?: boolean;
  /** The key to use for storing the in-progress draft in local storage. If not
   * provided, local storage is not used to save drafts. */
  draftLocalStorageKey?: string;

  // file meta data
  fileMeta?: any;
  // ====================
  // Multi comment review
  // ====================
  /** Determines whether multi-comment review functionality should be available
   * for this editor. If true (and if `pullRequestOpen` is true, the default
   * "Save" and "Cancel" buttons will be replaced with buttons to allow working
   * with multi-comment reviews. */
  multiCommentReview?: boolean;
  /** Indicates whether a multi-comment review is currently in process. When
   * multi-comment mode is active, passing true for this prop will change the
   * action buttons to allow adding the contents of this editor to an existing
   * review. Otherwise, the buttons will allow creating a new review or adding
   * the comment to the document immediately. */
  reviewInProgress?: boolean;
  /** Indicates whether the pull request is open. If this is not true,
   * multi-comment mode will not be enabled even if `multiCommentReview` is
   * true to discourage starting reviews on completed pull requests. */
  pullRequestOpen?: boolean;
  /** A function that will be called when a review is started, intended to
   * allow for publishing analytics events. */
  publishStartReviewAnalytics?: ClickEventWithSubjectArgs;

  // ================
  // Editor AI Plugin
  // ================
  isAIEnabled: boolean;
  workspaceUuid?: string;
  summarisePrDescriptionUrl?: string;

  // ===============
  // AI Opt In Modal
  // ===============
  isWorkspaceAdmin: boolean;

  // Provider that allows us to upload images into the editor
  legacyImageUploadProvider?: Promise<ImageUploadProvider>;
  // Provider that allows us to @ mention a user
  mentionProvider?: Promise<MentionProvider>;
  // Provider that enables emojis in editor
  emojiProvider?: Promise<EmojiResource>;
}

export interface State {
  isExpanded?: boolean;
  isEditing?: boolean;
}

interface DraftContextInterface {
  handleEditMode?: (isEditing: boolean) => void;
  handleDraftChanges?: () => void;
}

export const DraftContext = createContext<DraftContextInterface>({});

export default function BBCAtlaskitEditor(props: Props): JSX.Element {
  const {
    onOpen,
    onClose,
    onCancel,
    onSave,
    onChange,
    onEscapeHandler,
    onEditorReady,
    allowEmptyValue,
    showBeforeUnloadWarning,
    draftLocalStorageKey,
    defaultValue,
    placeholder,
    allowHelpButton,
    user,
    currentUserAccountId,
    isExpanded: isExpandedProp = false,
    isEditing: isEditingProp = false,
    intl,
    conversationsMentionContext,
    multiCommentReview,
    reviewInProgress,
    pullRequestOpen,
    isAIEnabled,
    workspaceUuid,
    summarisePrDescriptionUrl,
    publishStartReviewAnalytics,
    hideSecondaryToolbar,
    shouldFocus = true,
    isWorkspaceAdmin,
    allowRule = false,
    popupsMountPoint,
    legacyImageUploadProvider = undefined,
    emojiProvider = undefined,
    mentionProvider,
    fileMeta,
  } = props;

  const [isExpanded, setIsExpanded] = useState<boolean>(isExpandedProp);
  const [isEditing, setIsEditing] = useState<boolean>(isEditingProp);
  const [showAiOptInModal, setShowAiOptInModal] = useState<boolean>(false);
  const context = useContext(DraftContext);
  const [isFeedbackCollectorModalOpen, setIsFeedbackCollectorModalOpen] =
    useState(false);
  const [feedbackMetadata, setFeedbackMetadata] = useState<
    FeedbackMetadata | undefined
  >(undefined);
  const codeSuggestionsFF = useStatsigGate(
    StatsigFeatureKeys.codeSuggestions
  ) as boolean;

  const onFeedbackClicked = (metadata: FeedbackMetadata) => {
    setFeedbackMetadata(metadata);
    setIsFeedbackCollectorModalOpen(true);
  };

  const options: PluginOptions = {
    onFeedbackClicked,
  };

  const editorPluginAIProvider = useEditorProviderAI({
    options,
    workspaceUuid,
    summarisePrDescriptionUrl,
  });

  const handleOpen = useCallback(() => {
    setIsExpanded(true);

    if (typeof onOpen === 'function') {
      onOpen();
    }
  }, [onOpen, setIsExpanded]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setIsEditing(false);

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, setIsExpanded, setIsEditing]);

  useEffect(() => {
    if (isEditing) {
      handleOpen();
    }
  }, [isEditing, handleOpen]);

  const removeBeforeUnloadListener = () => {
    if (showBeforeUnloadWarning) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  };

  const removeUnsavedChanges = () => {
    if (draftLocalStorageKey) {
      store.remove(draftLocalStorageKey);
      if (typeof context.handleDraftChanges === 'function') {
        context.handleDraftChanges();
      }
    }
  };

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel();
      // Make sure to remove the listener since canceling
      // clears the editor content. This fixes a bug where the
      // alert was showing even though a user didn't have any
      // in-progress content in the current editor.
      removeBeforeUnloadListener();
    }
    handleClose();

    removeUnsavedChanges();
  };

  const handleSave = async (
    actions: any,
    pending = false,
    startReview = false
  ) => {
    if (typeof onSave === 'function') {
      const value = await actions.getValue();
      if (!value) {
        handleCancel();
        return;
      }

      // is only false if the content is blank/empty text
      const hasChars = value.content.some((n: JSONNode) =>
        n.content?.some(
          (val: JSONNode) => val.type !== 'text' || val.text?.trim()
        )
      );
      if (!hasChars) {
        value.content = [];
      }

      if (hasChars || allowEmptyValue) {
        if (startReview && typeof publishStartReviewAnalytics === 'function') {
          publishStartReviewAnalytics('StartReview');
        }
        onSave(value, pending);
        actions.clear();
        removeBeforeUnloadListener();
      } else {
        handleCancel();
        return;
      }
    }

    removeUnsavedChanges();
    handleClose();
  };

  const isEditorValueEmpty = (value: any): boolean => {
    return (
      !value?.content ||
      value.content.length === 0 ||
      (value.content.length === 1 && value.content[0].content?.length === 0)
    );
  };

  const handleChange = async (actions: EditorActions) => {
    const value = await actions.getValue();

    if (isEditorValueEmpty(value)) {
      removeBeforeUnloadListener();
    } else {
      if (showBeforeUnloadWarning) {
        window.addEventListener('beforeunload', beforeUnloadHandler);
      }
    }

    if (typeof onChange === 'function') {
      onChange(value);
    }

    if (draftLocalStorageKey) {
      const sevenDayExpiry = new Date().getTime() + 7 * 24 * 60 * 60 * 1000;
      store.set(draftLocalStorageKey, value, sevenDayExpiry);

      if (typeof context.handleDraftChanges === 'function') {
        context.handleDraftChanges();
      }
    }
  };

  const EditorButtonsWrapper = styled.div({
    paddingTop: token('space.150', '12px'),
    paddingBottom: token('space.150', '12px'),
  });

  const renderAkEditor = (actions: EditorActions) => {
    const editorProps: EditorProps = {
      appearance: 'comment',
      shouldFocus,
      onChange: debounce(() => handleChange(actions), 250),
      defaultValue: store.get(draftLocalStorageKey) || defaultValue,
      allowHelpDialog: allowHelpButton,
      // disable block cards for slack links until a
      // bitbucket-transformer patch/fix is released
      linking: {
        smartLinks: {
          allowBlockCards: false,
          provider: smartCardProvider,
        },
      },
      legacyImageUploadProvider,
      emojiProvider,
      mentionProvider,
      contextIdentifierProvider: createContextIdentifierProvider({
        conversationsMentionContext,
      }),
      onEditorReady,
      allowRule,
      popupsMountPoint,
      ...EDITOR_CONFIG,
      allowExtension: true,
    };

    let multiCommentKeyboardShortcutEnabled = false;
    // whether we render the default "Save" and "Cancel" Atlaskit editor buttons or turn on keyboard shortcuts
    // for multi-comment review.
    // hideSecondaryToolbar: used for the create PR description editor
    // multiCommentReview: feature context that is true for pull request page and false for branch and commit pages
    // pullRequestOpen: true if a PR is in the open status, false for merged or declined PRs
    // reviewInProgress: true if a multi-comment review is in progress
    // composableEditorUpgrade: feature flag to enable keyboard shortcuts and the ComposableEditor
    if (!hideSecondaryToolbar) {
      if (multiCommentReview) {
        if (reviewInProgress) {
          // Add to pending review
          multiCommentKeyboardShortcutEnabled = true;
          editorProps.onSave = () => handleSave(actions, true);
          editorProps.onCancel = handleCancel;
        } else if (pullRequestOpen) {
          // Start review
          multiCommentKeyboardShortcutEnabled = true;
          editorProps.onSave = () => handleSave(actions, true, true);
          editorProps.onCancel = handleCancel;
        } else {
          // Default editor actions (No review in progress and PR is not open)
          editorProps.onSave = () => handleSave(actions);
          editorProps.onCancel = handleCancel;
        }
      } else {
        // Default editor actions if not multicomment review
        editorProps.onSave = () => handleSave(actions);
        editorProps.onCancel = handleCancel;
      }
    }

    const AddCommentButtons = () => (
      <ButtonGroup>
        <Button appearance="primary" onClick={() => handleSave(actions, true)}>
          {intl.formatMessage(messages.addCommentButton)}
        </Button>
        <Button appearance="subtle-link" onClick={handleCancel}>
          Cancel
        </Button>
      </ButtonGroup>
    );

    const MultiCommentButtons = () => (
      <>
        <ButtonGroup>
          <Button
            appearance="primary"
            onClick={() => handleSave(actions, true, true)}
          >
            {intl.formatMessage(messages.startReviewButton)}
          </Button>
          <Button
            onClick={() => handleSave(actions)}
            testId="comment-save-button"
          >
            {intl.formatMessage(messages.addCommentNowButton)}
          </Button>
          <Button appearance="subtle-link" onClick={handleCancel}>
            {intl.formatMessage(messages.cancelButton)}
          </Button>
        </ButtonGroup>
      </>
    );

    return (
      <div>
        <AkCollapsibleComposableEditor
          editorProps={editorProps}
          placeholder={placeholder}
          isExpanded={isExpanded}
          handleOpen={handleOpen}
          multiCommentKeyboardShortcutEnabled={
            multiCommentKeyboardShortcutEnabled
          }
          editorPluginAIProvider={editorPluginAIProvider}
          aiGlobalOptIn={{
            status: isAIEnabled ? 'enabled' : 'disabled-opt-in',
            triggerOptInFlow: () => setShowAiOptInModal(true),
          }}
          fileMeta={fileMeta}
          codeSuggestionsFF={codeSuggestionsFF}
        />

        {multiCommentReview && isExpanded && (
          <EditorButtonsWrapper data-testid="ak-editor-secondary-toolbar">
            {reviewInProgress ? (
              <AddCommentButtons />
            ) : pullRequestOpen ? (
              <MultiCommentButtons />
            ) : null}
          </EditorButtonsWrapper>
        )}
      </div>
    );
  };

  // Allow exiting of PR Inline Edit Description via Esc Key.
  // Must be done at dom level higher than AkEditor to ensure we don't exit if
  // user is attempting to close Typeahead Menu via Esc Key.
  const onEscapeKey = (event: React.KeyboardEvent) => {
    if (
      typeof onEscapeHandler === 'function' &&
      (event.key === 'Escape' || event.key === 'Esc')
    ) {
      onEscapeHandler(event);
    }
  };

  function renderAvatar(): JSX.Element | null {
    if (isEditing) {
      return null;
    }

    return (
      <AvatarSection>
        <AkAvatar src={user && user.avatarUrl} />
      </AvatarSection>
    );
  }

  return (
    <EditorContext>
      <Container>
        {renderAvatar()}
        <EditorSection onKeyDown={onEscapeKey}>
          <WithEditorActions render={renderAkEditor} />
        </EditorSection>
      </Container>
      <FeedbackCollectorContext.Provider
        value={{
          isFeedbackCollectorModalOpen,
          setIsFeedbackCollectorModalOpen,
          currentUserDisplayName: user?.name,
          currentUserAccountId,
          feedbackMetadata,
          intl,
        }}
      >
        <FeedbackCollector />
      </FeedbackCollectorContext.Provider>
      <AiOptInModal
        showAiOptInModal={showAiOptInModal}
        setShowAiOptInModal={setShowAiOptInModal}
        isWorkspaceAdmin={isWorkspaceAdmin}
      />
    </EditorContext>
  );
}
